import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Global axios interceptor: sanitize Pydantic validation error arrays into strings
// Prevents "Objects are not valid as a React child" when toast.error(err.response.data.detail) is called
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      error.response.data.detail = error.response.data.detail
        .map(e => (typeof e === 'object' ? (e.msg || JSON.stringify(e)) : String(e)))
        .join('; ');
    }
    return Promise.reject(error);
  }
);
import { Toaster } from './components/ui/sonner';
import { toast as sonnerToast } from 'sonner';
import { Toaster as HotToaster } from 'react-hot-toast';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import ScrollToTop from './components/ScrollToTop';
import BackendHealthBanner from './components/BackendHealthBanner';
// Guard: Redirect introducers away from admin-only routes (they should only use /dashboard, /applications, /profile)
const INTRODUCER_ALLOWED_PATHS = [
  '/dashboard', '/applications', '/application', '/drafts', '/profile',
  '/forgot-password', '/reset-password', '/client-portal', '/sign', '/client-form', '/login', '/introducer-login'
];
const IntroducerRouteGuard = ({ user }) => {
  const loc = useLocation();
  const nav = useNavigate();
  React.useEffect(() => {
    if (user?.role === 'introducer') {
      const p = loc.pathname;
      const allowed = INTRODUCER_ALLOWED_PATHS.some(prefix => p === prefix || p.startsWith(prefix + '/') || p.startsWith(prefix));
      if (!allowed) {
        nav('/dashboard', { replace: true });
      }
    }
  }, [loc.pathname, user, nav]);
  return null;
};
import Login from './components/Login';
import CookieConsent from './components/website/CookieConsent';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';
import { ChatNotificationProvider } from './components/notifications/ChatNotificationProvider';
import { CallbackNotificationProvider } from './components/notifications/CallbackNotificationProvider';
import { LanguageProvider } from './i18n/translations';
import './App.css';

// Loading component for lazy loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#00aeef] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Lazy loaded components - Core Dashboard & Management
const AdvancedReporting = lazy(() => import('./components/AdvancedReporting'));
const BarclaysAdminDashboard = lazy(() => import('./components/BarclaysAdminDashboard'));
const BarclaysClientManagement = lazy(() => import('./components/BarclaysClientManagement'));
const BarclaysApplicationsPipeline = lazy(() => import('./components/BarclaysApplicationsPipeline'));
const BarclaysTasksActivities = lazy(() => import('./components/BarclaysTasksActivities'));
const BarclaysDocumentsCenter = lazy(() => import('./components/BarclaysDocumentsCenter'));
const BarclaysSettings = lazy(() => import('./components/BarclaysSettings'));
const BarclaysClientForm = lazy(() => import('./components/BarclaysClientForm'));
const BarclaysClientView = lazy(() => import('./components/BarclaysClientView'));

// Lazy loaded - Authentication & Portals
const BrokerLogin = lazy(() => import('./components/BrokerLogin'));
const IntroducerLogin = lazy(() => import('./components/IntroducerLogin'));
const ClientPortalRouter = lazy(() => import('./components/ClientPortalRouter'));
const Register = lazy(() => import('./components/Register'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const ForgotEmail = lazy(() => import('./components/ForgotEmail'));
const ClientSigningPage = lazy(() => import('./components/ClientSigningPage'));
const PublicApplicationForm = lazy(() => import('./components/PublicApplicationForm'));
const DocumentRequirementsPage = lazy(() => import('./pages/DocumentRequirementsPage'));

// Lazy loaded - Client & Application Management
const Applications = lazy(() => import('./components/Applications'));
const ApplicationsDashboard = lazy(() => import('./components/ApplicationsDashboard'));
const UnifiedApplicationForm = lazy(() => import('./components/UnifiedApplicationForm'));
const PremiumFormWithRealSteps = lazy(() => import('./components/PremiumFormWithRealSteps'));
const MultiStepWizard = lazy(() => import('./components/MultiStepWizard'));

// Lazy loaded - Appointments & Calendar
const AppointmentsCardView = lazy(() => import('./components/AppointmentsCardView'));

// Lazy loaded - Financial Tools
const IncomeExpenses = lazy(() => import('./components/IncomeExpenses'));
const Calculators = lazy(() => import('./components/FinancialCalculatorsPage'));

// Lazy loaded - Product Wizards
const ProductLandingPage = lazy(() => import('./components/ProductLandingPage'));
const MortgageWizard = lazy(() => import('./components/MortgageWizard'));
const LoanWizard = lazy(() => import('./components/LoanWizard'));
const FinanceWizard = lazy(() => import('./components/FinanceWizard'));
const BuyToLetWizard = lazy(() => import('./components/BuyToLetWizard'));

// Lazy loaded - Account & Settings
const Accounts = lazy(() => import('./components/Accounts'));
const WhatsAppChat = lazy(() => import('./components/WhatsAppChat'));
const WhatsAppSettings = lazy(() => import('./components/WhatsAppSettings'));
const WhatsAppInbox = lazy(() => import('./components/WhatsAppInbox'));
const AuditLogs = lazy(() => import('./components/AuditLogs'));
const SignatureTrackingDashboard = lazy(() => import('./components/SignatureTrackingDashboard'));

// Lazy loaded - Analytics & Reporting
const MortgageProductComparison = lazy(() => import('./components/MortgageProductComparison'));
const ProductComparisonTool = lazy(() => import('./pages/ProductComparisonTool'));
const Activity = lazy(() => import('./components/Activity'));
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const LenderPerformanceDashboard = lazy(() => import('./components/LenderPerformanceDashboard'));
const LenderManagement = lazy(() => import('./components/LenderManagement'));
const ComplianceNotifications = lazy(() => import('./components/ComplianceNotifications'));
const LeadScoringDashboard = lazy(() => import('./components/LeadScoringDashboard'));
const ClientConversionFunnel = lazy(() => import('./components/ClientConversionFunnel'));
const ReferralTracking = lazy(() => import('./components/ReferralTracking'));
const MonthlyRevenueForecast = lazy(() => import('./components/MonthlyRevenueForecast'));
const CreditScoreMonitoring = lazy(() => import('./components/CreditScoreMonitoring'));

// Lazy loaded - Email & Marketing
const EmailTemplates = lazy(() => import('./components/EmailTemplates'));
const EmailSettings = lazy(() => import('./components/EmailSettings'));
const EmailLogs = lazy(() => import('./components/EmailLogs'));
const EmailAnalytics = lazy(() => import('./components/EmailAnalytics'));
const EmailTemplateBuilder = lazy(() => import('./components/email/EmailTemplateBuilder'));
const MarketingAutomation = lazy(() => import('./components/MarketingAutomation'));
const BlogManagement = lazy(() => import('./components/BlogManagement'));
const SMSManagement = lazy(() => import('./components/SMSManagement'));
const SMSCampaigns = lazy(() => import('./components/SMSCampaigns'));
const CommunicationLog = lazy(() => import('./components/CommunicationLog'));
const MessagesHub = lazy(() => import('./components/MessagesHub'));
const WhatsAppHub = lazy(() => import('./components/WhatsAppHub'));
const EmailHub = lazy(() => import('./components/EmailHub'));

// Lazy loaded - User & Team Management
const Introducers = lazy(() => import('./components/Introducers'));
const Brokers = lazy(() => import('./components/Brokers'));
const PassedApplications = lazy(() => import('./components/PassedApplications'));
const ComplianceTemplates = lazy(() => import('./components/ComplianceTemplates'));
const DraftHub = lazy(() => import('./components/DraftHub'));
const IntroducerAnalytics = lazy(() => import('./components/IntroducerAnalytics'));
const TaskManagement = lazy(() => import('./components/TaskManagement'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const CompanySettings = lazy(() => import('./components/CompanySettings'));

// Lazy loaded - Pipeline & Leads
const ApplicationPipelineNew = lazy(() => import('./components/ApplicationPipelineNew'));
const LeadScoring = lazy(() => import('./components/LeadScoring'));
const ComplianceDashboard = lazy(() => import('./components/ComplianceDashboard'));

// Lazy loaded - Portals
const ModernIntroducerPortal = lazy(() => import('./components/ModernIntroducerPortal'));
const SimplifiedClientPortal = lazy(() => import('./components/SimplifiedClientPortal'));

// Lazy loaded - Broker Features
const BrokerMessaging = lazy(() => import('./components/broker/BrokerMessaging'));
const BrokerNotifications = lazy(() => import('./components/broker/BrokerNotifications'));

// Lazy loaded - Property & CRM
const PropertyManagementDashboard = lazy(() => import('./components/crm/PropertyManagementDashboard'));
const PropertySearchEnhanced = lazy(() => import('./components/crm/PropertySearchEnhanced'));
const LiveChatDashboard = lazy(() => import('./components/crm/LiveChatDashboard'));
const ProductEnquiriesDashboard = lazy(() => import('./components/crm/ProductEnquiriesDashboard'));
const CallbackRequestsDashboard = lazy(() => import('./components/crm/CallbackRequestsDashboard'));

// Lazy loaded - Premium Forms
const PremiumBarclaysForm = lazy(() => import('./components/PremiumBarclaysForm'));
const ComprehensivePremiumForm = lazy(() => import('./components/ComprehensivePremiumForm'));
const CompletePremiumBarclaysForm = lazy(() => import('./components/CompletePremiumBarclaysForm'));
const PrivateLendNew = lazy(() => import('./components/PrivateLendNew'));
const EligibilityHub = lazy(() => import('./components/EligibilityHub'));
const LenderCriteriaAdmin = lazy(() => import('./components/LenderCriteriaAdmin'));

// Lazy loaded - Website Components
const MortgageCalculator = lazy(() => import('./components/website').then(m => ({ default: m.MortgageCalculator })));
const ClientPortalDashboard = lazy(() => import('./components/website').then(m => ({ default: m.ClientPortalDashboard })));
const HomepageRedesign = lazy(() => import('./components/website/HomepageRedesign'));
const TurkishMortgagesPage = lazy(() => import('./components/website/TurkishMortgagesPage'));
const CityMortgagePage = lazy(() => import('./components/website/CityMortgagePage'));
const AboutPageDark = lazy(() => import('./components/website/AboutPageDark'));
const ContactPageDark = lazy(() => import('./components/website/ContactPageDark'));
const CareersPageDark = lazy(() => import('./components/website/CareersPageDark'));
const WebsiteLeadsAdmin = lazy(() => import('./components/website/WebsiteLeadsAdmin'));
const MarketRatesPageDark = lazy(() => import('./components/website/MarketRatesPageDark'));
const AppointmentBooking = lazy(() => import('./components/website/AppointmentBooking'));
const PropertyManagementPage = lazy(() => import('./components/website/PropertyManagementPage'));
const ProductEnquiryPage = lazy(() => import('./components/website/ProductEnquiryPage'));
const PrivacyPolicyPage = lazy(() => import('./components/website/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./components/website/TermsPage'));
const ComplaintsPage = lazy(() => import('./components/website/ComplaintsPage'));
const AccessibilityPage = lazy(() => import('./components/website/AccessibilityPage'));
const BlogPage = lazy(() => import('./components/website/BlogPage'));
const BlogPostPage = lazy(() => import('./components/website/BlogPostPage'));
const BestRatesPage = lazy(() => import('./components/website/BestRatesPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const QuoteWizard = lazy(() => import('./components/website/QuoteWizard'));
const LocalLandingPage = lazy(() => import('./components/website/LocalLandingPage'));

// Lazy loaded - Product Pages
const ResidentialMortgagePage = lazy(() => import('./components/website/ProductPages').then(m => ({ default: m.ResidentialMortgagePage })));
const BuyToLetPage = lazy(() => import('./components/website/ProductPages').then(m => ({ default: m.BuyToLetPage })));
const RemortgagePage = lazy(() => import('./components/website/ProductPages').then(m => ({ default: m.RemortgagePage })));
const FirstTimeBuyerPage = lazy(() => import('./components/website/ProductPages').then(m => ({ default: m.FirstTimeBuyerPage })));
const PersonalLoanPage = lazy(() => import('./components/website/ProductPages').then(m => ({ default: m.PersonalLoanPage })));
const LifeInsurancePage = lazy(() => import('./components/website/ProductPages').then(m => ({ default: m.LifeInsurancePage })));
const CommercialPage = lazy(() => import('./components/website/ProductPages').then(m => ({ default: m.CommercialPage })));
const HomeInsurancePage = lazy(() => import('./components/website/ProductPages').then(m => ({ default: m.HomeInsurancePage })));

// Lazy loaded - Documents & Signatures
const DocumentSigningPage = lazy(() => import('./components/signatures/DocumentSigningPage'));
const DocumentTemplatesPage = lazy(() => import('./components/documents/DocumentTemplatesPage'));

// Lazy loaded - Settings
const EmailNotificationSettings = lazy(() => import('./components/settings/EmailNotificationSettings'));

// Lazy loaded - Pages
const LenderTemplatesManagement = lazy(() => import('./pages/LenderTemplatesManagement'));
const PropertySearchPage = lazy(() => import('./pages/PropertySearchPage'));
const PropertyValuationPage = lazy(() => import('./components/PropertyValuationPage'));

// Resolve the API base URL defensively. If the build-time
// REACT_APP_BACKEND_URL points to a different hostname than the page the user
// actually visited (e.g. the env var has `www.credys.co.uk` but the user
// visited `credys.co.uk`), the browser would treat the request as cross-origin
// and CORS-block it. We detect that mismatch at runtime and fall back to
// `window.location.origin` so calls are always same-origin in production.
//
// Preview deploys (where backend lives on a separate Emergent host) still work
// because the build-time env var hostname will match `window.location.host`.
function resolveBackendUrl() {
  const fromEnv = process.env.REACT_APP_BACKEND_URL;
  if (!fromEnv) return typeof window !== 'undefined' ? window.location.origin : '';
  if (typeof window === 'undefined') return fromEnv;
  try {
    const envHost = new URL(fromEnv).host;
    if (envHost && envHost !== window.location.host) {
      // Same root domain but different sub-host (e.g. www vs apex) — prefer
      // same-origin to avoid CORS pitfalls in customer-facing prod domains.
      const envRoot = envHost.replace(/^www\./, '');
      const winRoot = window.location.host.replace(/^www\./, '');
      if (envRoot === winRoot) return window.location.origin;
    }
  } catch (_e) {
    // Malformed REACT_APP_BACKEND_URL — fall through to env value.
  }
  return fromEnv;
}

const BACKEND_URL = resolveBackendUrl();
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

function App() {
  // Hydrate user immediately from localStorage so refreshes don't flash the login page.
  // checkAuth() will validate/refresh this in the background.
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('cachedUser');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Listen for service-worker updates from index.js and surface a
  // friendly Sonner toast so the user can opt-in to reload at a safe moment
  // rather than mid-form-submission.
  useEffect(() => {
    const onUpdate = (e) => {
      const activate = e.detail?.activate;
      sonnerToast('A new version of Credys is available', {
        description: 'Reload now to load the latest improvements.',
        duration: Infinity,
        action: {
          label: 'Reload',
          onClick: () => activate && activate(),
        },
        cancel: {
          label: 'Later',
          onClick: () => {},
        },
      });
    };
    window.addEventListener('credys:update-available', onUpdate);
    return () => window.removeEventListener('credys:update-available', onUpdate);
  }, []);

  // Token refresh function
  const refreshToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;

      const response = await axios.post(`${API}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      console.log('[Auth] Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('[Auth] Token refresh failed:', error.message);
      return false;
    }
  };

  // Check if token needs refresh (refresh if less than 1 day remaining)
  const shouldRefreshToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;
      const oneDayInSeconds = 24 * 60 * 60;
      return timeUntilExpiry < oneDayInSeconds;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    // Check for stored token
    const token = localStorage.getItem('token');
    if (token) {
      // Check if token is expired before making request
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp && payload.exp < currentTime) {
          console.log('Token expired, removing...');
          localStorage.removeItem('token');
          localStorage.removeItem('cachedUser');
          setLoading(false);
          return;
        }
      } catch (e) {
        console.log('Invalid token format, removing...');
        localStorage.removeItem('token');
        localStorage.removeItem('cachedUser');
        setLoading(false);
        return;
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token with backend
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  // Set up periodic token refresh (every 6 hours)
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (token && shouldRefreshToken(token)) {
        console.log('[Auth] Token expiring soon, refreshing...');
        await refreshToken();
      }
    }, 6 * 60 * 60 * 1000); // Check every 6 hours

    // Also check on initial load
    const token = localStorage.getItem('token');
    if (token && shouldRefreshToken(token)) {
      refreshToken();
    }

    return () => clearInterval(refreshInterval);
  }, [user]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Auth check successful:', response.data);
      setUser(response.data);
      try { localStorage.setItem('cachedUser', JSON.stringify(response.data)); } catch {}
    } catch (error) {
      console.error('Auth check failed:', error.response?.status, error.response?.data?.detail || error.message);
      localStorage.removeItem('token');
      localStorage.removeItem('cachedUser');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      console.log('[Auth] Attempting login for:', email, 'Remember me:', rememberMe);
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        remember_me: rememberMe
      });

      const { access_token, user: userData } = response.data;
      console.log('[Auth] Login successful, user role:', userData?.role);
      localStorage.setItem('token', access_token);
      try { localStorage.setItem('cachedUser', JSON.stringify(userData)); } catch {}
      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberMe');
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      console.log('[Auth] User state set, token stored');
      return { success: true, role: userData?.role };
    } catch (error) {
      console.error('[Auth] Login failed:', error.response?.data?.detail || error.message);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('cachedUser');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const handleAuthError = () => {
    console.log('Handling authentication error - logging out user');
    logout();
  };

  // Merge updates into the current `user` object (and the cachedUser blob in
  // localStorage) without forcing a full re-fetch. Used after self-serve
  // mutations like password change so the in-memory `must_change_password`
  // flag matches the DB and the forced-change modal stops re-opening.
  const updateUser = (partial) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...(partial || {}) };
      try { localStorage.setItem('cachedUser', JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };

  const authContextValue = {
    user,
    login,
    logout,
    updateUser,
    handleAuthError,
    isAuthenticated: !!user,
    token: localStorage.getItem('token')
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      <QueryClientProvider client={queryClient}>
      <Router>
        <ScrollToTop />
        <BackendHealthBanner />
        <IntroducerRouteGuard user={user} />
        <ChatNotificationProvider>
        <CallbackNotificationProvider>
        <div className="App min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/login"
              element={user ? <Navigate to="/dashboard" replace /> : <Login />}
            />
            <Route
              path="/register"
              element={user ? <Navigate to="/dashboard" replace /> : <Register />}
            />
            <Route
              path="/forgot-password"
              element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />
            <Route
              path="/forgot-email"
              element={user ? <Navigate to="/dashboard" replace /> : <ForgotEmail />}
            />
            <Route
              path="/broker-login"
              element={user ? <Navigate to="/dashboard" replace /> : <BrokerLogin />}
            />
            <Route
              path="/introducer-login"
              element={user ? <Navigate to="/dashboard" replace /> : <IntroducerLogin />}
            />
            <Route
              path="/client-portal/*"
              element={<ClientPortalRouter />}
            />
            {/* Public Digital Signing Page - No Auth Required */}
            <Route
              path="/sign/:token"
              element={<ClientSigningPage />}
            />
            {/* Public Client Application Form - No Auth Required */}
            <Route
              path="/client-form/:token"
              element={<PublicApplicationForm />}
            />
            {/* Public Document Requirements Page - No Auth Required */}
            <Route
              path="/document-requirements"
              element={<DocumentRequirementsPage />}
            />
            <Route
              path="/applications"
              element={user ? <ApplicationsDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new"
              element={user ? <Applications /> : <Navigate to="/login" replace />}
            />
            {/* Product-specific application routes */}
            <Route
              path="/applications/new/personal-loan"
              element={user ? <MultiStepWizard productType="personal_loan" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/car-finance"
              element={user ? <MultiStepWizard productType="car_finance" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/residential"
              element={user ? <MultiStepWizard productType="residential" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/buy-to-let"
              element={user ? <BuyToLetWizard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/remortgage"
              element={user ? <MultiStepWizard productType="remortgage" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/right-to-buy"
              element={user ? <MultiStepWizard productType="right_to_buy" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/shared-ownership"
              element={user ? <MultiStepWizard productType="shared_ownership" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/first-homes"
              element={user ? <MultiStepWizard productType="first_homes" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/commercial"
              element={user ? <MultiStepWizard productType="commercial" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/second-charge"
              element={user ? <MultiStepWizard productType="second_charge" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/self-build"
              element={user ? <MultiStepWizard productType="self_build" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/green-mortgage"
              element={user ? <MultiStepWizard productType="green_mortgage" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/guarantor"
              element={user ? <MultiStepWizard productType="guarantor" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/business-loan"
              element={user ? <MultiStepWizard productType="business_loan" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/startup-loan"
              element={user ? <MultiStepWizard productType="startup_loan" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/bridging-loan"
              element={user ? <MultiStepWizard productType="bridging_loan" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/new/fact-find"
              element={user ? <MultiStepWizard productType="fact_find" /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/applications/:id"
              element={user ? <UnifiedApplicationForm /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/dashboard"
              element={
                user ? (
                  user.role === 'introducer' ? <ModernIntroducerPortal /> :
                  user.role === 'client' ? <SimplifiedClientPortal /> :
                  <BarclaysAdminDashboard />
                ) : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/advanced-reporting"
              element={user ? <AdvancedReporting /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/clients"
              element={user ? <BarclaysClientManagement /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/clients/new"
              element={user ? <BarclaysClientForm /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/clients/:id"
              element={user ? <BarclaysClientView /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/clients/:id/edit"
              element={user ? <BarclaysClientForm /> : <Navigate to="/login" replace />}
            />
            {/* Removed duplicate /applications route - using first one with Applications component */}
            <Route
              path="/applications-pipeline"
              element={user ? <BarclaysApplicationsPipeline /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/pipeline"
              element={user ? <ApplicationPipelineNew /> : <Navigate to="/login" replace />}
            />
            {/* Deprecated: Lender Conditions moved to client profile (Conditions tab) */}
            <Route
              path="/lender-conditions"
              element={<Navigate to="/clients" replace />}
            />
            <Route
              path="/lender-templates"
              element={user ? <LenderTemplatesManagement /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/mortgage-products"
              element={user ? <MortgageProductComparison /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/product-comparison-tool"
              element={user ? <ProductComparisonTool /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/appointments"
              element={user ? <AppointmentsCardView /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/income-expenses"
              element={user ? <IncomeExpenses /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/products"
              element={user ? <ProductLandingPage /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/calculators"
              element={user ? <Calculators /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/mortgage-wizard"
              element={user ? <MortgageWizard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/loan-wizard"
              element={user ? <LoanWizard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/finance-wizard"
              element={user ? <FinanceWizard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/buy-to-let-application"
              element={user ? <BuyToLetWizard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/whatsapp"
              element={user ? <WhatsAppHub /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/whatsapp/chat"
              element={user ? <WhatsAppChat /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/whatsapp/settings"
              element={user ? <WhatsAppSettings /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/whatsapp/inbox"
              element={user ? <WhatsAppInbox /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/signature-tracking"
              element={user ? <SignatureTrackingDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/audit-logs"
              element={user && user.role === 'admin' ? <AuditLogs /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/activity"
              element={user ? <Activity /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/email-templates"
              element={user && user.role === 'admin' ? <EmailTemplates /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/email-settings"
              element={user && user.role === 'admin' ? <EmailSettings /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/email-settings"
              element={user && user.role === 'admin' ? <EmailSettings /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/email-logs"
              element={user ? <EmailLogs /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/communication-log"
              element={user ? <CommunicationLog /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/messages"
              element={user ? <MessagesHub /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/email"
              element={user ? <EmailHub /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/email-analytics"
              element={user ? <EmailAnalytics /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/email-builder"
              element={user ? <EmailTemplateBuilder /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/introducers"
              element={user ? <Introducers /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/brokers"
              element={user ? <Brokers /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/passed-applications"
              element={user ? <PassedApplications /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/compliance-templates"
              element={user ? <ComplianceTemplates /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/drafts"
              element={user ? <DraftHub /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/introducer-analytics"
              element={user ? <IntroducerAnalytics /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/tasks"
              element={user ? <TaskManagement /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/analytics"
              element={user ? <AnalyticsDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/lender-performance"
              element={user ? <LenderPerformanceDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/lender-management"
              element={user ? <LenderManagement /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/compliance-notifications"
              element={user ? <ComplianceNotifications /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/lead-scores"
              element={user ? <LeadScoringDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/conversion-funnel"
              element={user ? <ClientConversionFunnel /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/referrals"
              element={user ? <ReferralTracking /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/revenue-forecast"
              element={user ? <MonthlyRevenueForecast /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/credit-monitoring"
              element={user ? <CreditScoreMonitoring /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/lead-scoring"
              element={user ? <LeadScoring /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/compliance"
              element={user ? <ComplianceDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/eligibility"
              element={user ? <EligibilityHub /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/lender-criteria"
              element={user ? <LenderCriteriaAdmin /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/marketing"
              element={user ? <MarketingAutomation /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/blog-manager"
              element={user ? <BlogManagement /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/sms-management"
              element={user ? <SMSManagement /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/sms-campaigns"
              element={user ? <SMSCampaigns /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/users"
              element={user && user.role === 'admin' ? <Navigate to="/user-management" replace /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/user-management"
              element={user && user.role === 'admin' ? <UserManagement /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/tasks"
              element={user ? <BarclaysTasksActivities /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/documents"
              element={user ? <BarclaysDocumentsCenter /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/accounts"
              element={user ? <Accounts /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/private-lend"
              element={user ? <PrivateLendNew /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/settings"
              element={user ? <BarclaysSettings /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/website-leads"
              element={user ? <WebsiteLeadsAdmin /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/product-enquiries"
              element={user ? <ProductEnquiriesDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/callback-requests"
              element={user ? <CallbackRequestsDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/settings/email-notifications"
              element={user ? <EmailNotificationSettings /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/settings/company"
              element={user ? <CompanySettings /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/digital-signatures"
              element={user ? <DocumentSigningPage /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/document-templates"
              element={user ? <DocumentTemplatesPage /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/live-chat"
              element={user ? <LiveChatDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/broker/messages"
              element={user ? <BrokerMessaging /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/broker/notifications"
              element={user ? <BrokerNotifications /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/test-premium-form"
              element={<PremiumBarclaysForm />}
            />
            <Route
              path="/test-comprehensive-form"
              element={<ComprehensivePremiumForm />}
            />
            <Route
              path="/test-complete-premium-form"
              element={<CompletePremiumBarclaysForm />}
            />
            <Route
              path="/test-real-steps-premium"
              element={<PremiumFormWithRealSteps />}
            />
            <Route
              path="/property-management-portal"
              element={<PropertyManagementDashboard />}
            />
            <Route
              path="/property-search"
              element={user ? <PropertySearchPage /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/property-portfolio"
              element={user ? <PropertySearchEnhanced /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/property-valuation"
              element={user ? <PropertyValuationPage /> : <Navigate to="/login" replace />}
            />
            {/* Public Website Routes */}
            <Route
              path="/home"
              element={
                <LanguageProvider>
                  <HomepageRedesign />
                </LanguageProvider>
              }
            />
            <Route
              path="/website"
              element={
                <LanguageProvider>
                  <HomepageRedesign />
                </LanguageProvider>
              }
            />
            <Route
              path="/mortgages-for-turkish-citizens"
              element={
                <LanguageProvider>
                  <TurkishMortgagesPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/mortgages/:slug"
              element={
                <LanguageProvider>
                  <CityMortgagePage />
                </LanguageProvider>
              }
            />
            <Route
              path="/calculator"
              element={
                <LanguageProvider>
                  <MortgageCalculator />
                </LanguageProvider>
              }
            />
            <Route
              path="/tools"
              element={<Navigate to="/calculator" replace />}
            />
            <Route
              path="/about"
              element={
                <LanguageProvider>
                  <AboutPageDark />
                </LanguageProvider>
              }
            />
            <Route
              path="/contact"
              element={
                <LanguageProvider>
                  <ContactPageDark />
                </LanguageProvider>
              }
            />
            <Route
              path="/careers"
              element={
                <LanguageProvider>
                  <CareersPageDark />
                </LanguageProvider>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <LanguageProvider>
                  <PrivacyPolicyPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/terms"
              element={
                <LanguageProvider>
                  <TermsPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/complaints"
              element={
                <LanguageProvider>
                  <ComplaintsPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/accessibility"
              element={
                <LanguageProvider>
                  <AccessibilityPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/blog"
              element={
                <LanguageProvider>
                  <BlogPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/news"
              element={
                <LanguageProvider>
                  <NewsPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/blog/:slug"
              element={
                <LanguageProvider>
                  <BlogPostPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/get-quote"
              element={
                <LanguageProvider>
                  <QuoteWizard />
                </LanguageProvider>
              }
            />
            <Route
              path="/mortgage-broker/:city"
              element={
                <LanguageProvider>
                  <LocalLandingPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/best-rates"
              element={
                <LanguageProvider>
                  <BestRatesPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/mortgages"
              element={
                <LanguageProvider>
                  <MortgageCalculator />
                </LanguageProvider>
              }
            />
            <Route
              path="/portal"
              element={
                <LanguageProvider>
                  <ClientPortalDashboard />
                </LanguageProvider>
              }
            />
            <Route
              path="/client-portal-new"
              element={
                <LanguageProvider>
                  <ClientPortalDashboard />
                </LanguageProvider>
              }
            />
            <Route
              path="/market-rates"
              element={
                <LanguageProvider>
                  <MarketRatesPageDark />
                </LanguageProvider>
              }
            />
            <Route
              path="/book-appointment"
              element={
                <LanguageProvider>
                  <AppointmentBooking />
                </LanguageProvider>
              }
            />
            <Route
              path="/property-management"
              element={
                <LanguageProvider>
                  <PropertyManagementPage />
                </LanguageProvider>
              }
            />
            
            {/* Product Enquiry Pages */}
            <Route
              path="/enquiry/residential-mortgage"
              element={
                <LanguageProvider>
                  <ResidentialMortgagePage />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/buy-to-let"
              element={
                <LanguageProvider>
                  <BuyToLetPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/remortgage"
              element={
                <LanguageProvider>
                  <RemortgagePage />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/first-time-buyer"
              element={
                <LanguageProvider>
                  <FirstTimeBuyerPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/personal-loan"
              element={
                <LanguageProvider>
                  <PersonalLoanPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/life-insurance"
              element={
                <LanguageProvider>
                  <LifeInsurancePage />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/commercial"
              element={
                <LanguageProvider>
                  <CommercialPage />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/home-insurance"
              element={
                <LanguageProvider>
                  <HomeInsurancePage />
                </LanguageProvider>
              }
            />
            {/* New Product Enquiry Routes */}
            <Route
              path="/enquiry/bridging-loan"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="bridging-loan" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/second-charge"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="second-charge" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/shared-ownership"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="shared-ownership" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/right-to-buy"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="right-to-buy" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/self-build"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="self-build" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/green-mortgage"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="green-mortgage" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/guarantor-mortgage"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="guarantor-mortgage" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/asset-finance"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="asset-finance" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/invoice-finance"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="invoice-finance" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/car-finance"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="car-finance" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/equity-release"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="equity-release" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/hmo-mortgage"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="hmo-mortgage" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/holiday-let"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="holiday-let" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/development-finance"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="development-finance" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/expat-mortgage"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="expat-mortgage" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/adverse-credit"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="adverse-credit" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/interest-only"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="interest-only" />
                </LanguageProvider>
              }
            />
            <Route
              path="/enquiry/debt-consolidation"
              element={
                <LanguageProvider>
                  <ProductEnquiryPage productType="debt-consolidation" />
                </LanguageProvider>
              }
            />
            
            <Route
              path="/"
              element={<Navigate to={user ? "/dashboard" : "/home"} replace />}
            />
          </Routes>
          </Suspense>
          <CookieConsent />
          <PWAInstallPrompt />
          <Toaster />
          <HotToaster position="top-right" />
          <ToastContainer
            position="bottom-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
          />
        </div>
        </CallbackNotificationProvider>
        </ChatNotificationProvider>
      </Router>
      </QueryClientProvider>
    </AuthContext.Provider>
  );
}

export default App;